import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Handshake, ArrowRight } from "lucide-react";
import { PageShell } from "@/components/Navbar";
import { Fade } from "@/components/proptech";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STAGE_STYLES = {
  Requested: "bg-attention-soft text-attention", Accepted: "bg-info-soft text-info",
  "Viewing Scheduled": "bg-info-soft text-info", "Offer Submitted": "bg-info-soft text-info",
  Closed: "bg-success-soft text-success",
};

export default function Collaborations() {
  const { user } = useAuth();
  const [list, setList] = useState([]);

  useEffect(() => { api.get("/collaborations").then((r) => setList(r.data)); }, []);

  return (
    <PageShell testid="collaborations-page">
      <Fade>
        <div className="mb-6">
          <p className="eyebrow flex items-center gap-2"><Handshake className="h-4 w-4" /> Collaborations</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Your deal rooms</h1>
          <p className="text-slate-500 mt-1">Every co-broke conversation you're part of.</p>
        </div>
      </Fade>

      {list.length === 0 && (
        <Fade delay={0.05}>
          <div className="bg-slate-50 border border-dashed border-border rounded-2xl p-12 text-center max-w-2xl">
            <Handshake className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="font-display text-lg font-semibold text-slate-800">No collaborations yet</p>
            <p className="text-slate-500 mt-1">Find a match, then hit <span className="font-medium text-slate-700">Collaborate</span> to open a deal room.</p>
            <div className="flex gap-3 justify-center mt-5">
              <Link to="/have-buyer" className="bg-primary text-primary-foreground px-4 py-2.5 rounded-xl font-medium text-sm">I Have a Buyer</Link>
              <Link to="/have-property" className="bg-accent text-accent-foreground px-4 py-2.5 rounded-xl font-medium text-sm">I Have a Property</Link>
            </div>
          </div>
        </Fade>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="collaborations-list">
        {list.map((c, i) => {
          const others = (c.participant_profiles || []).filter((p) => p && p.id !== user?.id);
          return (
            <Fade key={c.id} delay={0.04 * i} className="min-w-0">
              <Link to={`/collaboration/${c.id}`} data-testid={`collab-${c.id}`} className="group block min-w-0 bg-card border border-border rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <div className="relative h-28"><img src={c.property_snapshot?.image} alt="" className="h-full w-full object-cover" />
                  <span className={`absolute top-2 left-2 text-[11px] font-semibold px-2.5 py-1 rounded-full ${STAGE_STYLES[c.stage] || "bg-secondary"}`}>{c.stage}</span>
                  <span className="absolute top-2 right-2 bg-white/95 text-accent font-bold text-sm px-2.5 py-1 rounded-full">{c.match_score}% match</span>
                </div>
                <div className="p-4">
                  <div className="font-display font-semibold text-slate-900 truncate">{c.property_snapshot?.title}</div>
                  <div className="text-sm text-slate-500 mt-0.5 truncate">for buyer: {c.buyer_snapshot?.buyer_alias}</div>
                  <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-border">
                    <div className="flex items-center gap-2 min-w-0">
                      {others.slice(0, 2).map((o) => <img key={o.id} src={o.avatar} alt="" className="h-7 w-7 rounded-full object-cover border-2 border-white shrink-0" />)}
                      <span className="text-xs text-slate-500 truncate">{others.map((o) => o.name?.split(" ")[0]).join(", ")}</span>
                    </div>
                    <span className="shrink-0 text-accent text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">Open <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </div>
              </Link>
            </Fade>
          );
        })}
      </div>
    </PageShell>
  );
}
